import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useRef, useEffect } from "react";
import {
  FiShoppingCart,
  FiSearch,
  FiLogIn,
  FiMenu,
  FiHome,
  FiX,
} from "react-icons/fi";

const FloatingBottomNav = ({ logged_in = false }) => {
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  return (
      <motion.nav
        ref={navRef}
        animate={open ? "open" : "closed"}
        initial="closed"
        className="bg-ssbeige w-full text-ssblue flex items-center justify-between fixed top-0 left-[50%] -translate-x-[50%]"
      >
        <MenuButton setOpen={setOpen} open={open} />
        <div className="flex justify-between items-center gap-5 px-6">
          <Link url="/home" text="Home" />
          <Link url='/pricing' text="Pricing" />
          {logged_in ? (
            <Link url="/projects" text="Dashboard" />
          ) : (
            <Link url="/signup" text="Sign Up" />
          )}
          <img className="h-8" src="https://images.shosay.com/shosay-logo-bubblegum.png" alt="Shosay Logo" />
        </div>
        <Menu logged_in={logged_in} />
      </motion.nav>
  );
};

const Link = ({ url, text, Icon }) => {
  return (
    <a href={url} className="text-sm flex gap-2 items-center" >
      <span className="font-semibold">{text}</span>
    </a>
  );
};

const MenuButton = ({ open, setOpen }) => {
  return (
    <div
      onClick={() => setOpen((pv) => !pv)}
      className="text-xl font-bold h-full bg-ssorange text-white"
    >
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="p-4"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="icon-1"
              className="block"
              variants={iconVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.125, ease: "linear" }}
            >
              <FiX />
            </motion.span>
          ) : (
            <motion.span
              key="icon-2"
              className="block"
              variants={iconVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.125, ease: "linear" }}
            >
              <FiMenu />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

const Menu = ({ logged_in }) => {
  return (
    <motion.div
      variants={menuVariants}
      style={{ transformOrigin: "top", x: "-50%" }}
      className="p-4 bg-ssbeige-alt shadow-lg absolute top-[125%] left-[50%] flex flex-col gap-3 w-[calc(100vw_-_48px)]"
    >
      <MenuLink url="/home" text="Home" />
      {logged_in ? (
        <MenuLink url="/projects" text="Back to Dashboard" />
      ) : (
        <>
          <MenuLink url="/login" text="Login" />
          <MenuLink url="/signup" text="Sign Up" />
        </>
      )}
      <MenuLink url="/feedback" text="Support" />
      <MenuLink url="/pricing" text="Pricing" />
    </motion.div>
  );
};

const MenuLink = ({ text, url }) => {
  return (
    <motion.a
      variants={menuLinkVariants}
      href={url}
      rel="nofollow"
      className="text-sm hover:text-indigo-500 transition-colors flex items-center gap-2"
    >
      {text}
    </motion.a>
  );
};

export { FloatingBottomNav };

const iconVariants = {
  initial: { rotate: 180, opacity: 0 },
  animate: { rotate: 0, opacity: 1 },
  exit: { rotate: -180, opacity: 0 },
};

const menuVariants = {
  open: {
    scale: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.05,
    },
  },
  closed: {
    scale: 0,
    transition: {
      when: "afterChildren",
      staggerChildren: 0.05,
    },
  },
};

const menuLinkVariants = {
  open: {
    y: 0,
    opacity: 1,
  },
  closed: {
    y: -15,
    opacity: 0,
  },
};
